import { useEffect, useState } from "react";
import { useKeyPressEvent } from "react-use";

interface UseKeyboardNavigationProps {
  isOpen: boolean;
  maxIndex: number;
  onSelect: (index: number) => void;
  onClose: () => void;
}

const scrollToHighlightedOption = (index: number) => {
  const element = document.querySelector(
    `[data-option-index="${index}"]`
  ) as HTMLElement;
  element?.scrollIntoView({
    block: "nearest",
    behavior: "smooth",
  });
};

export const useKeyboardNavigation = ({
  isOpen,
  maxIndex,
  onSelect,
  onClose,
}: UseKeyboardNavigationProps) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  useKeyPressEvent("ArrowDown", () => {
    if (!isOpen) return;
    const newIndex =
      highlightedIndex < maxIndex ? highlightedIndex + 1 : maxIndex;
    setHighlightedIndex(newIndex);
    scrollToHighlightedOption(newIndex);
  });
  useKeyPressEvent("ArrowUp", () => {
    if (!isOpen) return;
    const newIndex = highlightedIndex > 0 ? highlightedIndex - 1 : 0;
    setHighlightedIndex(newIndex);
    scrollToHighlightedOption(newIndex);
  });
  useKeyPressEvent("Enter", () => {
    if (!isOpen) return;
    if (highlightedIndex < 0 || highlightedIndex > maxIndex) return;
    onSelect(highlightedIndex);
  });
  useKeyPressEvent("Escape", () => {
    if (!isOpen) return;
    onClose();
  });

  useEffect(() => {
    if (isOpen) {
      scrollToHighlightedOption(-1);
    }
  }, [highlightedIndex, isOpen]);

  return {
    highlightedIndex,
    resetHighlightedIndex: () => setHighlightedIndex(-1),
  };
};
